import { FC, HTMLAttributes, MouseEvent as ReactMouseEvent, useCallback, useMemo, useState } from "react";

import Stack from "@mui/material/Stack";
import { useController, useFormContext } from "react-hook-form";

import { useRateCategoryCounts } from "../../../RatesDigest/RatesDigestContext";
import { useRateFieldName } from "../../hooks/useRateFieldName";

import { EditCategoryModal } from "./EditCategoryModal";
import { NewCategoryModal } from "./NewCategoryModal";

import { useTranslate } from "@/common/hooks/useTranslate";
import {
  AutocompleteOption,
  AutocompleteOptionSize,
  AutocompleteV2Option,
  DashboardAutocompleteV2,
} from "@/dashboard-design-system/DashboardAutocompleteV2";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { RateCategory, useRateCategories } from "@/features/LiveFormEditor/Steps/StepTickets/hooks/useRateCategories";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";
import { Add, Edit } from "@/icons/outlined";

type CategoryOption = AutocompleteV2Option<string>;

interface CategoriesFieldProps {
  variant?: "default" | "inline";
}

export const CategoriesField: FC<CategoriesFieldProps> = ({ variant = "default" }) => {
  const { t } = useTranslate();
  const { control, getValues, setValue } = useFormContext<StepTicketsFormData>();
  const { categories, getCategoryName } = useRateCategories();

  const fieldName = useRateFieldName("categoryIds");

  const countByCategoryId = useRateCategoryCounts();

  const {
    field: { value: selectedIds, onChange },
  } = useController({ control, name: fieldName });

  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<RateCategory | null>(null);

  const options = useMemo<CategoryOption[]>(
    () => categories.map((category) => ({ label: getCategoryName(category), value: category.id })),
    [categories, getCategoryName]
  );

  const categoryById = useMemo(() => {
    const map = new Map<string, RateCategory>();
    for (const category of categories) {
      map.set(category.id, category);
    }
    return map;
  }, [categories]);

  const selectedOptions = useMemo<CategoryOption[]>(() => {
    const ids = (selectedIds as string[] | undefined) ?? [];
    return ids
      .map((id) => options.find((option) => option.value === id))
      .filter((option): option is CategoryOption => Boolean(option));
  }, [options, selectedIds]);

  const handleChange = useCallback(
    (value: CategoryOption[] | undefined) => {
      onChange((value ?? []).map((option) => option.value));
    },
    [onChange]
  );

  const handleCreated = useCallback(
    (categoryId: string) => {
      const currentIds = (selectedIds as string[] | undefined) ?? [];
      if (!currentIds.includes(categoryId)) {
        onChange([...currentIds, categoryId]);
      }
    },
    [onChange, selectedIds]
  );

  const handleDeleted = useCallback(
    (deletedId: string) => {
      const currentRates = getValues("rates") ?? [];
      currentRates.forEach((rate, index) => {
        const ids = rate.categoryIds ?? [];
        if (ids.includes(deletedId)) {
          setValue(
            `rates.${index}.categoryIds`,
            ids.filter((id) => id !== deletedId),
            { shouldDirty: true }
          );
        }
      });
    },
    [getValues, setValue]
  );

  const optionSize: AutocompleteOptionSize = variant === "inline" ? "small" : "standard";

  const renderOption = useCallback(
    (liProps: HTMLAttributes<HTMLLIElement>, option: CategoryOption, { selected }: { selected: boolean }) => {
      const category = categoryById.get(option.value);
      if (!category) {
        return null;
      }
      const count = countByCategoryId.get(option.value) ?? 0;
      return (
        <AutocompleteOption
          {...liProps}
          key={option.value}
          label={option.label}
          secondaryLabel={count > 0 ? t("dashboard", "ticketing.categories.applied.itemCount", { count }) : undefined}
          checkbox
          checked={selected}
          selected={selected}
          size={optionSize}
          action={
            <DashboardIconButton
              size="small"
              vibe="neutral"
              aria-label={t("dashboard", "ticketing.categories.applied.rename")}
              onMouseDown={preventRowSelection}
              onClick={(event) => {
                preventRowSelection(event);
                setEditingCategory(category);
              }}
            >
              <Edit />
            </DashboardIconButton>
          }
        />
      );
    },
    [categoryById, countByCategoryId, optionSize, t]
  );

  const autocomplete = (
    <DashboardAutocompleteV2<CategoryOption, true>
      multiple
      name={fieldName}
      size={variant === "inline" ? "small" : "medium"}
      optionSize={optionSize}
      label={variant === "inline" ? undefined : t("dashboard", "ticketing.categories.field.label")}
      tooltipText={variant === "inline" ? undefined : t("dashboard", "ticketing.categories.field.tooltip")}
      placeholder={t("dashboard", "ticketing.categories.field.placeholder")}
      searchPlaceholder={t("dashboard", "ticketing.categories.field.searchPlaceholder")}
      options={options}
      value={selectedOptions}
      onChange={handleChange}
      noOptionsText={<></>}
      openOnFocus
      chipProps={{ shape: "rounded", variant: "filled" }}
      slotProps={{ paper: { sx: { "& .MuiAutocomplete-noOptions": { display: "none" } } } }}
      sx={{ "& .MuiInputBase-input": { caretColor: "auto !important" } }}
      renderOption={renderOption}
      footer={{
        type: "alternate",
        actions: [
          {
            label: t("dashboard", "ticketing.categories.field.newCategory"),
            icon: <Add />,
            onClick: () => setIsNewModalOpen(true),
          },
        ],
      }}
    />
  );

  return (
    <>
      {variant === "inline" ? (
        <Stack sx={{ flexGrow: 1, minWidth: 0, width: { sm: 300 }, maxWidth: 300 }}>{autocomplete}</Stack>
      ) : (
        autocomplete
      )}
      <NewCategoryModal
        open={isNewModalOpen}
        onClose={() => setIsNewModalOpen(false)}
        onCreated={(category) => handleCreated(category.id)}
      />
      <EditCategoryModal
        open={editingCategory !== null}
        category={editingCategory}
        onClose={() => setEditingCategory(null)}
        onDeleted={handleDeleted}
      />
    </>
  );
};

const preventRowSelection = (event: ReactMouseEvent) => {
  event.preventDefault();
  event.stopPropagation();
};
